import { http } from './client';

// =============================================================================
// Common
// =============================================================================
export interface PaginatedResponse<T> {
  data: T[];
  pagination: { page: number; page_size: number; total: number; total_pages: number };
}

// =============================================================================
// Beneficiaries (M02)
// =============================================================================
export interface BeneficiarySummary {
  stages: Record<string, number>;
  schemes: { GOAT: number; SHEEP: number; PIG: number };
}
export interface Beneficiary {
  id: string;
  name: string;
  category: string;
  scheme: 'GOAT' | 'SHEEP' | 'PIG';
  scheme_name: string;
  district: string;
  block: string | null;
  status: string;
  amount_inr: number;
  applied_at: string;
  verified_at: string | null;
}
export interface BeneficiaryDetail {
  id: string;
  name: string;
  category: string;
  mobile: string;
  aadhaar_masked: string | null;
  bank_account_masked: string | null;
  scheme: { code: string; name: string; unit_cost_inr: number };
  location: { mandal: string; district: string; block: string | null; gram_panchayat: string | null };
  status: string;
  amount_inr: number;
  deposit_inr: number;
  pfms_reference: string | null;
  lifecycle: Record<string, string | null>;
  events: Array<{ event_type: string; actor_role_code: string | null; occurred_at: string; details: unknown }>;
}

export const beneficiariesApi = {
  summary: async (): Promise<BeneficiarySummary> => (await http.get('/beneficiaries/summary')).data.data,
  list: async (params: { page?: number; page_size?: number; scheme?: string; stage?: string; search?: string } = {}): Promise<PaginatedResponse<Beneficiary>> =>
    (await http.get('/beneficiaries', { params })).data,
  detail: async (code: string): Promise<BeneficiaryDetail> =>
    (await http.get(`/beneficiaries/${code}`)).data.data,
};

// =============================================================================
// Poultry (M03)
// =============================================================================
export interface PoultrySummary {
  stages: Record<string, { count: number; total_dpr_lakhs: number }>;
}
export interface PoultryApplication {
  id: string;
  applicant: string;
  entity_type: string;
  sector: string;
  dpr_lakhs: number;
  stage: string;
  submitted_at: string;
  district: string;
  mandal: string;
}
export interface PoultryDetail extends PoultryApplication {
  pan: string | null;
  gstin: string | null;
  approved_lakhs: number | null;
  timeline: Record<string, string | null>;
  notes: string | null;
  rejection_reason: string | null;
}

export const poultryApi = {
  summary: async (): Promise<PoultrySummary> => (await http.get('/poultry-applications/summary')).data.data,
  list: async (params: { page?: number; page_size?: number; stage?: string } = {}): Promise<PaginatedResponse<PoultryApplication>> =>
    (await http.get('/poultry-applications', { params })).data,
  detail: async (code: string): Promise<PoultryDetail> =>
    (await http.get(`/poultry-applications/${code}`)).data.data,
  advance: async (code: string, body: { notes?: string; approved_amount_inr_lakhs?: number } = {}): Promise<{ from: string; to: string }> =>
    (await http.post(`/poultry-applications/${code}/advance`, body)).data.data,
  revise: async (code: string, notes: string): Promise<{ stage: string }> =>
    (await http.post(`/poultry-applications/${code}/revise`, { notes })).data.data,
  reject: async (code: string, reason: string): Promise<{ action: string }> =>
    (await http.post(`/poultry-applications/${code}/reject`, { reason })).data.data,
};

// =============================================================================
// MAITRI (M04)
// =============================================================================
export interface MaitriSummary {
  total: number; active: number; inactive: number;
  weekly_reporting: number; weekly_reporting_pct: number;
  services_30d: number; synced_to_bharat_pashudhan: number;
}
export interface MaitriWorker {
  id: string;
  name: string;
  active: boolean;
  gp: string | null;
  district: string;
  services_30d: number;
  last_service_at: string | null;
}

export const maitriApi = {
  summary: async (): Promise<MaitriSummary> => (await http.get('/maitri/summary')).data.data,
  list: async (params: { page?: number; page_size?: number; status?: string } = {}): Promise<PaginatedResponse<MaitriWorker>> =>
    (await http.get('/maitri', { params })).data,
  detail: async (code: string): Promise<MaitriWorkerDetail> =>
    (await http.get(`/maitri/${code}`)).data.data,
};

export interface MaitriWorkerDetail {
  id: string;
  name: string;
  mobile: string | null;
  active: boolean;
  training_batch: string | null;
  certified_at: string | null;
  bharat_pashudhan_id: string | null;
  last_service_at: string | null;
  location: { mandal: string; district: string; gp: string | null };
  services_by_type: Record<string, number>;
  recent_services: Array<{
    type: string;
    performed_at: string;
    animal_type: string | null;
    animal_tag: string | null;
    beneficiary_name: string | null;
    synced: boolean;
  }>;
}

// =============================================================================
// Mela (M05)
// =============================================================================
export interface MelaSummary {
  total: number; scheduled: number; in_progress: number; completed: number;
  cancelled: number; beneficiaries_served: number;
}
export interface MelaCamp {
  id: string; date: string; status: string;
  target: number; actual: number | null;
  lead: string; services: string[] | null;
  np: string | null; district: string; mandal: string;
}

export const melaApi = {
  summary: async (): Promise<MelaSummary> => (await http.get('/mela/summary')).data.data,
  list: async (params: { page?: number; page_size?: number; status?: string } = {}): Promise<PaginatedResponse<MelaCamp>> =>
    (await http.get('/mela', { params })).data,
  detail: async (code: string): Promise<MelaCampDetail> =>
    (await http.get(`/mela/${code}`)).data.data,
};

export interface MelaCampDetail {
  id: string;
  date: string;
  status: string;
  target: number;
  actual: number | null;
  lead: { name: string };
  location: { mandal: string; district: string; np: string | null };
  services_planned: string[] | null;
  notes: string | null;
}

// =============================================================================
// Exports
// =============================================================================
export interface ExportJob {
  id: string;
  template_code: string;
  name: string;
  format: 'xlsx' | 'pdf' | 'csv';
  status: string;
  row_count: number | null;
  size_bytes: number | null;
  duration_ms: number | null;
  username: string;
  full_name: string;
  requested_at: string;
  completed_at: string | null;
}

export const exportsApi = {
  recent: async (limit = 15): Promise<ExportJob[]> =>
    (await http.get('/exports/recent', { params: { limit } })).data.data,
  /**
   * Triggers download. Returns the export code (from X-Export-Code header).
   * Resolves once the browser kicks off the file download.
   */
  download: async (path: string, suggestedName: string): Promise<string> => {
    const res = await http.post(path, {}, { responseType: 'blob' });
    const blob = res.data as Blob;
    const code = String(res.headers['x-export-code'] ?? '');
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = code ? `${code}-${suggestedName}` : suggestedName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return code;
  },
};

// =============================================================================
// Recommendations
// =============================================================================
export interface Recommendation {
  id: string;
  category: string;
  source: string;
  target: string;
  target_role: string | null;
  text: string;
  primary_action: string;
  confidence: number;
  status: string;
  created_at: string;
  decided_at: string | null;
  outcome: string | null;
}
export interface RecommendationDetail extends Recommendation {
  model_version: string | null;
  reasoning_trace: Array<{ step: number; title: string; detail: string }>;
  metadata: unknown;
}

export const recommendationsApi = {
  list: async (params: { status?: string; page_size?: number; min_confidence?: number } = {}): Promise<PaginatedResponse<Recommendation>> =>
    (await http.get('/recommendations', { params })).data,
  detail: async (code: string): Promise<RecommendationDetail> =>
    (await http.get(`/recommendations/${code}`)).data.data,
  decide: async (code: string, decision: string, outcome?: string): Promise<{ status: string }> =>
    (await http.post(`/recommendations/${code}/decide`, { decision, outcome })).data.data,
};

// =============================================================================
// Actions Due
// =============================================================================
export interface ActionItem {
  id: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
  type: string;
  title: string;
  count: number;
  desc: string;
  deadline: string;
  overdue: boolean;
  module: string;
  link: string;
  submitter: string;
}
export interface ActionsDueResponse {
  actions: ActionItem[];
  summary: { critical: number; high: number; medium: number; low: number };
}

export const actionsApi = {
  due: async (): Promise<ActionsDueResponse> => (await http.get('/actions/due')).data.data,
};

// =============================================================================
// Users
// =============================================================================
export interface User {
  id: string;
  username: string;
  full_name: string;
  email: string | null;
  designation: string | null;
  status: 'active' | 'suspended' | 'pending';
  last_login_at: string | null;
  must_change_password: boolean;
  role: { code: string; name: string; tier: string } | null;
  scope: { type: string; id: number | null } | null;
}

export interface Role {
  code: string;
  name: string;
  tier: string;
  admin_level: string;
}

export interface CreateUserBody {
  username: string;
  full_name: string;
  email?: string;
  mobile?: string;
  designation?: string;
  role_code: string;
  scope_type: 'state' | 'mandal' | 'district' | 'block' | 'gram_panchayat' | 'nyaya_panchayat' | 'self';
  scope_id?: number;
}

export const usersApi = {
  list: async (params: { status?: string; tier?: string; search?: string } = {}): Promise<User[]> =>
    (await http.get('/users', { params })).data.data,
  roles: async (): Promise<Role[]> =>
    (await http.get('/users/roles')).data.data,
  create: async (body: CreateUserBody): Promise<{ id: string; username: string; temporary_password: string; message: string }> =>
    (await http.post('/users', body)).data.data,
  setStatus: async (id: string, status: 'active' | 'suspended' | 'pending'): Promise<void> => {
    await http.patch(`/users/${id}/status`, { status });
  },
  resetPassword: async (id: string): Promise<{ temporary_password: string }> =>
    (await http.post(`/users/${id}/reset-password`)).data.data,
};

// Auth — change password
// =============================================================================
export const authExtrasApi = {
  changePassword: async (current_password: string, new_password: string): Promise<{ ok: boolean }> =>
    (await http.post('/auth/change-password', { current_password, new_password })).data,
};

// =============================================================================
// Trends
// =============================================================================
export interface TrendRow {
  month: string;
  month_start: string;
  [key: string]: string | number | null;
}

export interface ForecastRow {
  month: string;
  month_start: string;
  actual: number | null;
  predicted: number | null;
  upper: number | null;
  lower: number | null;
}

export interface ForecastModel {
  name: string;
  version: string;
  slope: number;
  intercept: number;
  confidence_interval: string;
  training_window_months: number;
}

export interface Anomaly {
  id: string;
  severity: string;
  module: string;
  type: string;
  title: string;
  location: string;
  triggered_at: string;
  status: string;
}

export const trendsApi = {
  schemeUptake: async (months = 12): Promise<TrendRow[]> =>
    (await http.get('/dashboard/trends/scheme-uptake', { params: { months } })).data.data,
  slaPerformance: async (months = 12): Promise<TrendRow[]> =>
    (await http.get('/dashboard/trends/sla-performance', { params: { months } })).data.data,
  servicesByType: async (months = 12): Promise<TrendRow[]> =>
    (await http.get('/dashboard/trends/services-by-type', { params: { months } })).data.data,
  forecast: async (): Promise<{ data: ForecastRow[]; model: ForecastModel }> =>
    (await http.get('/dashboard/trends/forecast')).data,
  anomalies: async (): Promise<Anomaly[]> =>
    (await http.get('/dashboard/trends/anomalies')).data.data,
};

// =============================================================================
// Mela schedule
// =============================================================================
export interface ScheduleMelaBody {
  scheduled_date: string;
  district_id: number;
  mandal_id: number;
  nyaya_panchayat_id?: number;
  lead_officer_name: string;
  target_beneficiaries: number;
  services_planned: string[];
  notes?: string;
}

export const melaScheduleApi = {
  pickerMandals: async (): Promise<Array<{ id: number; code: string; name: string }>> =>
    (await http.get('/mela/picker-data/mandals')).data.data,
  pickerDistricts: async (mandalId: number): Promise<Array<{ id: number; name: string }>> =>
    (await http.get(`/mela/picker-data/districts/${mandalId}`)).data.data,
  schedule: async (body: ScheduleMelaBody): Promise<{ id: number; code: string; status: string }> =>
    (await http.post('/mela', body)).data.data,
};
